import React from 'react';
import { cn } from '../../../lib/cn';

export interface DashboardToggleOption<T extends string> {
  id: T;
  label: string;
}

export interface DashboardToggleGroupProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: DashboardToggleOption<T>[];
  /** Reference demo toggle uses 11px horizontal; time-range bar uses 12px. */
  padX?: 11 | 12;
  className?: string;
}

export function DashboardToggleGroup<T extends string>({
  value,
  onChange,
  options,
  padX = 11,
  className,
}: DashboardToggleGroupProps<T>) {
  const padClass = padX === 12 ? 'px-3' : 'px-[11px]';

  return (
    <div
      className={cn(
        'inline-flex gap-px rounded-[9px] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-0.5',
        className,
      )}
      role="tablist"
    >
      {options.map((option) => {
        const active = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.id)}
            className={cn(
              'cursor-pointer rounded-[7px] border-0 py-[5px] text-[11.5px] leading-none tracking-[-0.005em]',
              'transition-[background,color,box-shadow] duration-[180ms]',
              padClass,
              active
                ? 'bg-[var(--cream-1)] font-semibold text-[var(--ink-0)] shadow-[inset_0_0_0_1px_var(--cream-2)]'
                : 'bg-transparent font-medium text-[var(--ink-1)]',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
